<template>
  <div class="arp-select-itens">
    <b-loading :active="loadingItens" :is-full-page="false" />
    <p class="arp-select-itens__hint">{{ hintText }}</p>

    <b-field grouped class="arp-select-itens__toolbar column-direction-touch">
      <b-field expanded label="Filtrar por número do item" class="arp-select-itens__filtro">
        <b-input
          v-model="filtro"
          placeholder="Ex.: 1, 15, 25-35"
          icon-right="magnify"
          @input="aplicarFiltro"
        />
      </b-field>

      <b-field label="Agrupar por Fornecedor" class="arp-select-itens__agrupar">
        <b-switch v-model="agruparPorFornecedor">
          {{ agruparPorFornecedor ? 'Sim' : 'Não' }}
        </b-switch>
      </b-field>
    </b-field>

    <!-- Flat (padrão) -->
    <div v-if="!agruparPorFornecedor">
      <div v-if="!itensFiltradosFlat.length" class="panel">
        <div class="panel-block is-justify-content-center">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>Nenhum item.</p>
            </div>
          </section>
        </div>
      </div>

      <b-table
        v-else
        checkable
        hoverable
        class="table is-fullwidth arp-select-itens__table"
        :data="itensFiltradosFlat"
        :checked-rows.sync="checkedFlat"
        :custom-is-checked="mesmoItem"
        @check="onCheckFlat"
        @check-all="onCheckFlat"
      >
        <template #empty>
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>Nenhum item.</p>
            </div>
          </section>
        </template>

        <b-table-column field="numero" label="ID" width="64" v-slot="props">
          {{ props.row.numero }}
        </b-table-column>

        <b-table-column field="descricao" label="Descrição" v-slot="props">
          {{ props.row.descricao }}
        </b-table-column>

        <b-table-column field="vencedor" label="Vencedor" v-slot="props">
          {{ props.row.vencedor || '—' }}
        </b-table-column>
      </b-table>
    </div>

    <!-- Agrupado por fornecedor -->
    <div v-else class="arp-select-itens__groups">
      <div v-if="!gruposFiltrados.length" class="panel">
        <div class="panel-block is-justify-content-center">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>Nenhum item.</p>
            </div>
          </section>
        </div>
      </div>

      <template v-else>
        <div class="arp-select-itens__groups-toolbar">
          <b-checkbox
            :value="todosFiltradosSelecionados"
            :indeterminate="algunsFiltradosSelecionados"
            @input="toggleTodosFlat"
          >
            Selecionar todos
          </b-checkbox>
        </div>

        <div
          v-for="grupo in gruposFiltrados"
          :key="grupo.fornecedorId"
          class="arp-select-itens__group panel"
        >
          <div
            class="panel-heading arp-select-itens__group-head"
            @click="toggleExpand(grupo.fornecedorId)"
          >
            <b-icon
              :icon="expanded[grupo.fornecedorId] ? 'chevron-down' : 'chevron-right'"
              size="is-small"
            />
            <b-checkbox
              :value="grupoTodosSelecionados(grupo)"
              :indeterminate="grupoParcial(grupo)"
              @click.native.stop
              @input="val => toggleGrupo(grupo, val)"
            />
            <strong>{{ grupo.fornecedorNome }}</strong>
            <span class="tag is-light">
              {{ contarSelecionados(grupo) }} de {{ grupo.itens.length }} itens
            </span>
          </div>

          <div v-if="expanded[grupo.fornecedorId]" class="panel-block arp-select-itens__group-body">
            <b-table
              checkable
              hoverable
              class="table is-fullwidth arp-select-itens__table"
              :data="grupo.itens"
              :checked-rows="checkedDoGrupo(grupo)"
              :custom-is-checked="mesmoItem"
              @check="list => onCheckGrupo(grupo, list)"
              @check-all="list => onCheckGrupo(grupo, list)"
            >
              <b-table-column field="numero" label="ID" width="64" v-slot="props">
                {{ props.row.numero }}
              </b-table-column>

              <b-table-column field="descricao" label="Descrição" v-slot="props">
                {{ props.row.descricao }}
              </b-table-column>
            </b-table>
          </div>
        </div>
      </template>
    </div>

    <p class="arp-select-itens__summary">
      {{ totalSelecionados }} de {{ totalItens }} itens selecionados
      <span v-if="filtroAtivo"> · filtrados</span>
    </p>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

function parseFiltro(raw) {
  if (!raw || !String(raw).trim()) return null;
  const set = new Set();
  String(raw)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .forEach(part => {
      const range = part.match(/^(\d+)\s*-\s*(\d+)$/);
      if (range) {
        const a = Number(range[1]);
        const b = Number(range[2]);
        for (let n = Math.min(a, b); n <= Math.max(a, b); n += 1) set.add(n);
      } else if (/^\d+$/.test(part)) {
        set.add(Number(part));
      }
    });
  return set.size ? set : null;
}

export default {
  name: 'ArpSelectItens',
  props: {
    /** Força agrupamento inicial (ex.: legado). Default flat. */
    mode: {
      type: String,
      default: 'flat',
      validator: v => ['flat', 'grouped'].includes(v),
    },
  },
  data() {
    return {
      filtro: '',
      numerosFiltro: null,
      expanded: {},
      agruparPorFornecedor: false,
      checkedFlat: [],
      syncingCheck: false,
      loadingItens: false,
    };
  },
  computed: {
    ...mapGetters({
      grupos: 'obterArpItensSelecao',
      flat: 'obterArpItensFlat',
      journey: 'obterArpJourney',
      draft: 'obterArpDraft',
    }),
    hintText() {
      if (this.journey?.type === 'reserva') {
        return this.agruparPorFornecedor
          ? 'Somente itens homologados de fornecedores vencedores (diferentes do licitante convocado). Cada grupo é um vencedor.'
          : 'Somente itens homologados de fornecedores vencedores (diferentes do licitante convocado). A coluna Vencedor é informativa.';
      }
      return this.agruparPorFornecedor
        ? 'Os grupos mostram o vencedor de cada item (informativo). Selecione os itens que farão parte da ARP.'
        : 'A coluna Vencedor é informativa. Selecione os itens que farão parte da ARP.';
    },
    filtroAtivo() {
      return !!this.numerosFiltro;
    },
    itensFiltradosFlat() {
      if (!this.numerosFiltro) return this.flat;
      return this.flat.filter(i => this.numerosFiltro.has(i.numero));
    },
    gruposFiltrados() {
      if (!this.numerosFiltro) return this.grupos;
      return this.grupos
        .map(g => ({
          ...g,
          itens: g.itens.filter(i => this.numerosFiltro.has(i.numero)),
        }))
        .filter(g => g.itens.length);
    },
    numerosVisiveis() {
      return new Set(this.itensFiltradosFlat.map(i => i.numero));
    },
    totalItens() {
      return this.flat.length;
    },
    totalSelecionados() {
      return this.flat.filter(i => i.selecionado).length;
    },
    todosFiltradosSelecionados() {
      const itens = this.itensFiltradosFlat;
      return itens.length > 0 && itens.every(i => i.selecionado);
    },
    algunsFiltradosSelecionados() {
      const itens = this.itensFiltradosFlat;
      const n = itens.filter(i => i.selecionado).length;
      return n > 0 && n < itens.length;
    },
  },
  watch: {
    itensFiltradosFlat: {
      immediate: true,
      handler(list) {
        if (this.syncingCheck) return;
        this.checkedFlat = list.filter(i => i.selecionado);
      },
    },
    agruparPorFornecedor(val) {
      if (val) {
        (this.grupos || []).forEach(g => {
          if (this.expanded[g.fornecedorId] === undefined) {
            this.$set(this.expanded, g.fornecedorId, false);
          }
        });
      }
    },
  },
  created() {
    this.agruparPorFornecedor = this.mode === 'grouped';
    this.carregarLista();
  },
  methods: {
    ...mapActions(['carregarArpItensSelecao', 'atualizarArpItensSelecao', 'atualizarArpDraft']),
    async carregarLista() {
      this.loadingItens = true;
      try {
        await this.carregarArpItensSelecao();
        (this.grupos || []).forEach(g => {
          this.$set(this.expanded, g.fornecedorId, false);
        });
      } finally {
        this.loadingItens = false;
      }
    },
    mesmoItem(a, b) {
      return a && b && a.numero === b.numero;
    },
    aplicarFiltro() {
      this.numerosFiltro = parseFiltro(this.filtro);
    },
    syncDraftSelecionados(grupos) {
      const nums = new Set();
      const ids = new Set();
      grupos.forEach(g => {
        g.itens.forEach(i => {
          if (!i.selecionado) return;
          nums.add(i.numero);
          const id = Number(i.id);
          if (Number.isFinite(id) && id > 0) ids.add(id);
        });
      });
      this.atualizarArpDraft({
        itensSelecionados: Array.from(nums).sort((a, b) => a - b),
        itensIds: Array.from(ids).sort((a, b) => a - b),
      });
    },
    aplicarSelecaoPorNumeros(selectedNums) {
      this.syncingCheck = true;
      const next = this.grupos.map(g => ({
        ...g,
        itens: g.itens.map(i => ({
          ...i,
          selecionado: this.numerosVisiveis.has(i.numero)
            ? selectedNums.has(i.numero)
            : i.selecionado,
        })),
      }));
      this.atualizarArpItensSelecao(next);
      this.syncDraftSelecionados(next);
      this.$nextTick(() => {
        this.syncingCheck = false;
        this.checkedFlat = this.itensFiltradosFlat.filter(i => i.selecionado);
      });
    },
    onCheckFlat(list) {
      const selected = new Set((list || []).map(i => i.numero));
      this.aplicarSelecaoPorNumeros(selected);
    },
    checkedDoGrupo(grupo) {
      return (grupo.itens || []).filter(i => i.selecionado);
    },
    onCheckGrupo(grupo, list) {
      const selected = new Set((list || []).map(i => i.numero));
      this.syncingCheck = true;
      const next = this.grupos.map(g => {
        if (g.fornecedorId !== grupo.fornecedorId) return g;
        return {
          ...g,
          itens: g.itens.map(i => ({
            ...i,
            selecionado: selected.has(i.numero),
          })),
        };
      });
      this.atualizarArpItensSelecao(next);
      this.syncDraftSelecionados(next);
      this.$nextTick(() => {
        this.syncingCheck = false;
      });
    },
    toggleTodosFlat(selecionado) {
      const visibles = this.numerosVisiveis;
      this.syncingCheck = true;
      const next = this.grupos.map(g => ({
        ...g,
        itens: g.itens.map(i => (visibles.has(i.numero) ? { ...i, selecionado } : i)),
      }));
      this.atualizarArpItensSelecao(next);
      this.syncDraftSelecionados(next);
      this.$nextTick(() => {
        this.syncingCheck = false;
        this.checkedFlat = this.itensFiltradosFlat.filter(i => i.selecionado);
      });
    },
    toggleGrupo(grupo, selecionado) {
      this.syncingCheck = true;
      const next = this.grupos.map(g => {
        if (g.fornecedorId !== grupo.fornecedorId) return g;
        return {
          ...g,
          itens: g.itens.map(i => ({ ...i, selecionado })),
        };
      });
      this.atualizarArpItensSelecao(next);
      this.syncDraftSelecionados(next);
      this.$nextTick(() => {
        this.syncingCheck = false;
      });
    },
    grupoTodosSelecionados(grupo) {
      return grupo.itens.length > 0 && grupo.itens.every(i => i.selecionado);
    },
    grupoParcial(grupo) {
      const n = this.contarSelecionados(grupo);
      return n > 0 && n < grupo.itens.length;
    },
    contarSelecionados(grupo) {
      return grupo.itens.filter(i => i.selecionado).length;
    },
    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },
  },
};
</script>

<style scoped>
.arp-select-itens__hint {
  color: #7a7a7a;
  margin-bottom: 1rem;
}

.arp-select-itens__toolbar {
  align-items: flex-end;
  margin-bottom: 0.75rem;
}

.arp-select-itens__toolbar > .field {
  margin-bottom: 0;
  margin-right: 0;
}

.arp-select-itens__toolbar > .field:not(:last-child) {
  margin-right: 0.75rem;
}

.arp-select-itens__toolbar >>> .label {
  display: block;
  line-height: 1.5em;
  margin-bottom: 0.5rem;
  min-height: 1.5em;
  white-space: nowrap;
}

.arp-select-itens__filtro {
  flex: 1 1 auto;
  min-width: 0;
}

.arp-select-itens__filtro >>> .control,
.arp-select-itens__filtro >>> .input {
  width: 100%;
  max-width: 100%;
}

.arp-select-itens__filtro >>> .input {
  height: 2.5em;
}

.arp-select-itens__agrupar {
  min-width: 7.5rem;
  white-space: nowrap;
}

.arp-select-itens__agrupar >>> .control {
  display: flex;
  align-items: center;
  min-height: 2.5em;
}

.arp-select-itens__agrupar >>> .switch {
  white-space: nowrap;
}

.arp-select-itens__table >>> .table td,
.arp-select-itens__table >>> .table th {
  vertical-align: middle;
}

.arp-select-itens__groups-toolbar {
  margin-bottom: 0.75rem;
}

.arp-select-itens__group {
  margin-bottom: 0.75rem;
}

.arp-select-itens__group-head {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.arp-select-itens__group-body {
  display: block;
  padding: 0;
}

.arp-select-itens__group-body >>> .b-table {
  width: 100%;
}

.arp-select-itens__summary {
  color: #2153b5;
  font-weight: 500;
  margin-top: 0.75rem;
}

@media (max-width: 700px) {
  .arp-select-itens__agrupar {
    min-width: 0;
  }
}
</style>
